import { z } from 'zod';

// Helper for Firestore Timestamp
export const FirestoreTimestampSchema = z.union([
  z.object({ seconds: z.number(), nanoseconds: z.number() }),
  z.object({ _seconds: z.number(), _nanoseconds: z.number() }),
]);

/** M-472: Zod schema for host's preferred time window (see PreferredWindow type). */
export const PreferredWindowSchema = z.object({
  preset: z.enum(['afternoon', 'evening', 'late', 'flexible']).optional(),
  start: z
    .string()
    .regex(/^\d{2}:\d{2}$/, 'Must be HH:mm')
    .optional(),
  end: z
    .string()
    .regex(/^\d{2}:\d{2}$/, 'Must be HH:mm')
    .optional(),
});

export const AvailabilityStatusSchema = z.enum(['free', 'maybe', 'busy']);

export const DayKeySchema = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
export const TimeKeySchema = z.enum(['morning', 'afternoon', 'evening', 'late']);

export const RecurringAvailabilitySchema = z.record(
  DayKeySchema,
  z.record(TimeKeySchema, AvailabilityStatusSchema)
);

// H-447: date keys are ISO dates (≤10 chars); time-slot labels are at most 20 chars.
export const UserAvailabilitySchema = z.record(
  z.string().max(10),
  z.record(z.string().max(20), AvailabilityStatusSchema)
);

// H-447: bound all location strings to prevent payload growth.
export const UserLocationSchema = z.object({
  country: z.string().max(100),
  city: z.string().max(100),
  address: z.string().max(200).optional(),
  postcode: z.string().max(20).optional(),
  // M-209: optional coordinates (written by onboarding geocoding).
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
});

// H-447: canonical source of truth for all preference fields — Functions imports this.
// Array and string limits from docs/specs/preference-payload-bounds-spec.md.
export const PreferencesSchema = z.object({
  budget: z.enum(['£', '££', '£££', '££££']).or(z.literal('')),
  vibe: z.array(z.string().max(100)).max(10),
  activityType: z.array(z.string().max(100)).max(20),
  time: z.array(z.string().max(100)).max(20),
  availability: z.union([z.array(z.string()), UserAvailabilitySchema]).optional(),
  location: z.array(z.string().max(200)).max(5).optional(),
  userLocation: UserLocationSchema.optional(),
  userInterests: z.array(z.string().max(100)).max(20).optional(),
  // Planning fields (previously Functions-only) — now canonical here.
  date: z.array(z.string().max(10)).max(31).optional(),
  numberOfGuests: z.number().int().min(1).max(100).optional(),
  consensusTime: z.string().max(10).optional(),
  consensusDate: z.string().max(10).optional(),
  locationMarkers: z
    .array(
      z.object({
        address: z.string().max(200),
        lat: z.number().min(-90).max(90),
        lng: z.number().min(-180).max(180),
        types: z.array(z.string().max(100)).max(20).optional(),
      })
    )
    .max(5)
    .nullish(),
  preferredWindow: PreferredWindowSchema.optional(),
});

// M-476: lenient adapter for reading legacy Firestore records that pre-date the
// bounds above. Never use this schema in submission handlers — it is read-only.
export const LegacyPreferencesSchema = z
  .object({
    budget: z.enum(['£', '££', '£££', '££££']).or(z.literal('')).optional(),
    vibe: z.array(z.string()).optional(),
    activityType: z.array(z.string()).optional(),
    time: z.array(z.string()).optional(),
    availability: z.union([z.array(z.string()), UserAvailabilitySchema]).optional(),
    location: z.array(z.string()).optional(),
    userLocation: UserLocationSchema.optional(),
    userInterests: z.array(z.string()).optional(),
  })
  .passthrough();

/** M-475/M-493: Strict schema for wizard inputs accepted by the createEventCallable CF.
 * Unknown keys at any level are rejected so the boundary fails closed. */
export const CreateEventInputSchema = z
  .object({
    name: z.string().min(1).max(100),
    hostName: z.string().min(1).max(80),
    description: z.string().max(500).optional(),
    eventType: z.enum(['date', 'hangout', 'trip']).optional(),
    date: z
      .array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/))
      .max(90)
      .default([]),
    expectedParticipants: z.number().int().min(1).max(100).optional(),
    locationMarkers: z
      .array(
        z
          .object({
            address: z.string().max(256),
            lat: z.number().min(-90).max(90),
            lng: z.number().min(-180).max(180),
            types: z.array(z.string().max(64)).max(20).optional(),
          })
          .strict()
      )
      .max(5)
      .optional(),
    preferredWindow: PreferredWindowSchema.strict().optional(),
  })
  .strict();

export type CreateEventInput = z.infer<typeof CreateEventInputSchema>;

export const ParticipantSchema = z.object({
  uid: z.string(),
  name: z.string(),
  role: z.enum(['host', 'guest']),
  joinedAt: FirestoreTimestampSchema.optional(),
  preferences: PreferencesSchema.optional(),
  hasVoted: z.boolean().optional(),
  candidateVotes: z.array(z.string()).optional(),
  complementaryVotes: z.array(z.string()).optional(),
  hasSetPreferences: z.boolean().optional(),
  guestAvailability: z.array(z.string()).optional(),
});

export const TravelFromPreviousSchema = z.object({
  durationMinutes: z.number().int().nonnegative(),
  distanceKm: z.number().nonnegative().optional(),
  mode: z.enum(['walk', 'transit', 'drive']).optional(),
});

export const StopReadinessSchema = z.object({
  booking: z
    .object({
      status: z.enum(['confirmed', 'recommended', 'not-required', 'unknown']),
      url: z.string().url().optional(),
    })
    .optional(),
  hours: z
    .object({
      status: z.enum(['open', 'closed', 'unknown']),
      checkedAt: z.string().datetime().optional(),
      stale: z.boolean().optional(),
    })
    .optional(),
  costPerPerson: z
    .object({ amount: z.number().nonnegative(), currency: z.literal('GBP') })
    .optional(),
  setting: z.enum(['indoor', 'outdoor', 'unknown']).optional(),
  contingency: z
    .object({
      name: z.string().min(1),
      googleMapsUri: z.string().url().optional(),
      travelTime: z.string().optional(),
    })
    .optional(),
});

export const ItineraryItemSchema = z.object({
  time: z.string(),
  activity: z.string(),
  cost: z.string(),
  description: z.string().optional(),
  image: z.string().nullable().optional(),
  rating: z.number().nullable().optional(),
  review: z.string().optional(),
  googleMapsUri: z.string().nullable().optional(),
  websiteUri: z.string().nullable().optional(),
  /** M-473: Canonical HH:mm start time without display annotations. */
  startTime: z.string().optional(),
  /** M-473: Canonical HH:mm end time. */
  endTime: z.string().optional(),
  /** M-473: Duration of this stop in minutes. */
  durationMinutes: z.number().int().nonnegative().optional(),
  /** M-473: Structured travel leg from the previous stop. */
  travelFromPrevious: TravelFromPreviousSchema.optional(),
  readiness: StopReadinessSchema.optional(),
});

export const RecommendationSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
  type: z.string(),
  budget: z.string(),
  narrative: z.string(),
  description: z.string().optional(),
  itinerary: z.array(z.union([z.string(), ItineraryItemSchema])).optional(),
  vibe: z.array(z.string()),
  location: z.string().optional(),
  date: z.string().optional(),
});

export const ScoredCandidateSchema = z.object({
  id: z.string(),
  name: z.string(),
  formattedAddress: z.string().optional(),
  shortFormattedAddress: z.string().optional(),
  score: z.number(),
  types: z.array(z.string()),
  priceLevel: z.string().optional(),
  estimatedCost: z.number(),
  rating: z.number().optional(),
  userRatingCount: z.number().optional(),
  normalizedRating: z.number().optional(),
  embedding: z.array(z.number()).optional(),
  open: z.boolean().optional(),
  openingHours: z.record(z.string(), z.unknown()).optional(),
  regularOpeningHours: z.record(z.string(), z.unknown()).optional(),
  currentOpeningHours: z.record(z.string(), z.unknown()).optional(),
  openingHoursCheckedAt: z.string().datetime().optional(),
  reservable: z.boolean().optional(),
  outdoorSeating: z.boolean().optional(),
  editorialSummary: z
    .object({ text: z.string().optional(), overview: z.string().optional() })
    .passthrough()
    .optional(),
  location: z.object({ latitude: z.number(), longitude: z.number() }).optional(),
  photos: z
    .array(z.object({ name: z.string(), authorAttributions: z.array(z.unknown()) }))
    .optional(),
  reviews: z
    .array(
      z.object({
        relativePublishTimeDescription: z.string(),
        rating: z.number(),
        text: z.object({ text: z.string() }),
        authorAttribution: z.object({ displayName: z.string(), photoUri: z.string() }),
      })
    )
    .optional(),
  websiteUri: z.string().optional(),
  googleMapsUri: z.string().optional(),
  image: z.string().optional(),
  url: z.string().optional(),
  budget: z.string().optional(),
  aiInsights: z
    .object({
      vibes: z.array(z.string()),
      sentiment: z.enum(['positive', 'neutral', 'negative']),
      suitability: z.array(z.string()),
      summary: z.string(),
    })
    .optional(),
});

export const EventSchema = z.object({
  id: z.string(),
  hostId: z.string(),
  hostName: z.string(),
  name: z.string(),
  description: z.string().optional(),
  eventType: z.enum(['date', 'hangout', 'trip']).optional(),
  _schemaVersion: z.number().optional(), // Added for Schema Versioning
  date: z.union([
    z.string(),
    z.object({ start: z.string(), end: z.string() }),
    z.array(z.string()),
  ]),
  // H-424: participant-proposed candidate dates (see types.ts)
  participantDates: z.array(z.string()).optional(),
  time: z.string().optional(),
  timeOfDay: z.array(z.string()).optional(),
  location: z.union([z.string(), z.array(z.string())]),

  // V2 Fields
  type: z.string().optional(),
  vibe: z.array(z.string()).optional(),
  expectedParticipants: z.number().optional(),
  // H-293: server-maintained count of joined participants (see types.ts)
  participantCount: z.number().int().nonnegative().optional(),

  status: z.enum([
    'planning',
    'ready',
    'generating',
    'candidates_ready',
    'generating_complementary',
    'complementary_ready',
    'generating_itinerary',
    'generated',
    'error',
  ]),

  createdAt: z.union([FirestoreTimestampSchema, z.string()]),

  candidates: z.array(ScoredCandidateSchema).optional(),
  selectedCandidateId: z.string().optional(),
  selectedCandidateIds: z.array(z.string()).optional(),

  complementaryCandidates: z.array(ScoredCandidateSchema).optional(),
  selectedComplementaryIds: z.array(z.string()).optional(),
  complementaryActivities: z.array(ScoredCandidateSchema).optional(),

  recommendations: z.array(RecommendationSchema).optional(),
  participants: z.array(ParticipantSchema).optional(),
  summaryHash: z.string().optional(),
  lastError: z
    .object({
      message: z.string(),
      timestamp: FirestoreTimestampSchema,
      failedAtStep: z.enum(['candidates', 'complementary', 'itinerary']).optional(),
    })
    .optional(),
  generatedAt: FirestoreTimestampSchema.optional(),
  preferredWindow: PreferredWindowSchema.optional(),
  votingDeadline: FirestoreTimestampSchema.optional(),
  votingStartedAt: FirestoreTimestampSchema.optional(),
  votingDurationHours: z.number().optional(),
  tieBreaker: z
    .object({
      coWinners: z.array(z.string()),
      resolvedBy: z.string().nullable(),
      resolvedAt: FirestoreTimestampSchema.optional(),
    })
    .nullable()
    .optional(),
  candidatesSource: z.enum(['regen', 'pipeline']).optional(),
  // H-318: leading candidate's Places photo resource name (see types.ts)
  coverPhotoRef: z.string().nullable().optional(),
});
